import GitBranchIcon from "@redotech/redo-icons/git-branch-01.svg";
import { Step } from "@redotech/redo-model/return-flow";
import { returnProcessingDecisionFromModel } from "@redotech/redo-model/return-flow-step-from-model";
import {
  isReturnProcessingDecisionStepValid,
  ReturnProcessingDecisionState,
} from "@redotech/redo-model/return-flow-step-valid";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { produce } from "immer";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";

export const RETURN_PROCESSING_DECISION = (
  Details: React.ComponentType<
    StepTypeDetailsProps<ReturnProcessingDecisionState>
  >,
): StepType<ReturnProcessingDecisionState, Step.ReturnProcessingDecision> => ({
  Details: (props) => React.createElement(Details, props),
  customTitle: (state) => state.customTitle,
  description: (state) => state.prompt,
  // Two outgoing edges. Index 0 = True (left handle), Index 1 = False (right
  // handle). Mirror the existing condition step's convention so the editor's
  // graph layout positions branches consistently.
  downstream(state) {
    return [
      { id: state.nextTrue, label: state.trueLabel || "True" },
      { id: state.nextFalse, label: state.falseLabel || "False" },
    ];
  },
  empty: {
    prompt: "",
    decisionKey: "",
    trueLabel: "Yes",
    falseLabel: "No",
    scope: "return_item",
  },
  fromModel: (model, id) => returnProcessingDecisionFromModel(model, id),
  Icon: GitBranchIcon,
  iconColor: "warning",
  layout: () => BlockLayout.FULL,
  maxDownstream: () => 2,
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.nextTrue === targetStepId) {
        draft.nextTrue = undefined;
      }
      if (draft.nextFalse === targetStepId) {
        draft.nextFalse = undefined;
      }
    });
  },
  stepDeleted(state, stepId) {
    return produce(state, (draft) => {
      if (draft.nextTrue === stepId) {
        draft.nextTrue = undefined;
      }
      if (draft.nextFalse === stepId) {
        draft.nextFalse = undefined;
      }
    });
  },
  title: "Decision",
  toModel(state, id) {
    return {
      type: Step.RETURN_PROCESSING_DECISION,
      customTitle: state.customTitle,
      prompt: state.prompt,
      decisionKey: state.decisionKey,
      trueLabel: state.trueLabel,
      falseLabel: state.falseLabel,
      nextTrueId: state.nextTrue ? id(state.nextTrue) : null,
      nextFalseId: state.nextFalse ? id(state.nextFalse) : null,
      scope: state.scope,
    };
  },
  updateConnection(state, handleIndex, targetStepId) {
    return produce(state, (draft) => {
      if (handleIndex === 0) {
        draft.nextTrue = targetStepId;
      } else if (handleIndex === 1) {
        draft.nextFalse = targetStepId;
      } else {
        throw new Error(
          `Invalid handle index ${handleIndex} for return-processing decision step`,
        );
      }
    });
  },
  valid: (state) => isReturnProcessingDecisionStepValid(state).valid,
});

export { type ReturnProcessingDecisionState } from "@redotech/redo-model/return-flow-step-valid";
